/**
 * A página de quadros editáveis: seções com prosa, aviso, tabela e Salvar próprios.
 *
 * <p><b>Isto era o corpo de "Cenas, Ações e Fluxo de Jogo"</b>, e saiu de lá quando a
 * página Personagem precisou do mesmo desenho. Duas cópias da gravação por quadro
 * seriam duas chances de uma delas esquecer a trava de versão — e a trava é a parte que
 * protege o trabalho de quem edita a dois. A tela dona continua decidindo o conteúdo:
 * quais seções existem, o texto de fábrica e as famílias de cor são dela; daqui saem o
 * estado, a gravação e o histórico, que são iguais nas duas.
 *
 * <p><b>Cada quadro se grava sozinho, com botão.</b> A primeira versão da tela de Cenas
 * gravou automaticamente e por documento inteiro, e isso perdeu trabalho: com duas
 * pessoas na página, a segunda a gravar levava junto os quadros como estavam quando ela
 * abriu, apagando o que a primeira tinha escrito — sem erro nenhum. Agora a escrita é
 * <b>por quadro</b> (o servidor mescla só aquela chave) e <b>explícita</b>, e o quadro
 * carrega a versão que leu: se alguém gravou no meio, o servidor recusa e a tela mostra
 * as duas.
 *
 * <p>Por isso estas páginas <b>não</b> usam o `useRascunho` das outras três: aquele
 * gancho grava o documento inteiro, que é exatamente o que causou o problema aqui.
 */
import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ErroApi } from '../../api/cliente';
import { TituloDaPagina } from '../../shell/TituloDaPagina';
import * as paginaApi from './paginaApi';
import { TabelaEditavel } from './TabelaEditavel';
import * as t from './tabelasEditaveis';
import { combinar } from './tabelasEditaveis';
import type { Linha, Tabela } from './tabelasEditaveis';
import './paginaDeQuadros.css';

/** O que a página dona declara sobre cada seção. */
export interface SecaoDeQuadros {
  titulo: string;
  /** Como o botão chama uma linha nova: "linha", "conflito", "perícia". */
  rotuloDoItem: string;
  /** Numera as linhas — usar quando a ordem <i>é</i> a informação. */
  numerada?: boolean;
  /** A moldura dourada — para a seção que é a razão da página existir. */
  destaque?: boolean;
  /**
   * As famílias oferecidas na coluna de cor <b>desta</b> seção.
   *
   * <p>Por seção, e não por página, porque o rótulo certo depende do assunto: o verde
   * que diz "Lugar" na tabela de ações diz "Coleta" na de perícias. O id continua o da
   * folha de estilo (`fam-*`) — a cor é a mesma, só o nome que a explica muda.
   *
   * <p>A coluna só aparece onde o quadro tem `familias`, como sempre foi: oferecê-la em
   * toda tabela faria a página perguntar "de que família é esta linha?" em tabelas
   * cujas linhas não têm família nenhuma.
   */
  cores?: { id: string; rotulo: string }[];
  /**
   * O ícone da seção — ao lado do título e no índice. Vem do conteúdo
   * ({@code iconesDeConteudo}), porque é a página que sabe que "Cansaço" é os três
   * zês e "A Carga" é o peso da Ficha.
   */
  icone?: ReactNode;
  /**
   * O ícone de cada linha da tabela, resolvido pelo texto dela. É o que pinta a espada
   * ao lado de "Espada" e a sigla colorida ao lado de "FOR" — e deixa em branco a linha
   * que o time inventou e o sistema não reconhece. Ver {@link TabelaEditavel}.
   */
  iconeDaLinha?: (linha: Linha) => ReactNode;
}

/**
 * Um macro-título da página, e os quadros que ficam debaixo dele.
 *
 * <p>Existe por causa de "Condições do Personagem": três famílias de condição, cada uma
 * com vários quadros, e um índice no alto para pular direto. O macro-título fica em
 * código — ele é estrutura da página, não conteúdo de um quadro, e não há documento onde
 * uma edição dele moraria. Os títulos dos quadros, esses são editáveis.
 */
export interface GrupoDeQuadros {
  id: string;
  titulo: string;
  /** As chaves de `tabelas` que ficam sob este macro-título, na ordem de exibição. */
  quadros: string[];
}

export function PaginaDeQuadros({
  chave,
  tabelas,
  secoes,
  entregas,
  subtitulo,
  abas,
  grupos,
}: {
  /** O nome da página no servidor (`cenas-acoes-fluxo`, `personagem-resumo`). */
  chave: string;
  /** O conteúdo de fábrica — o que quem abre a tela pela primeira vez encontra. */
  tabelas: Record<string, Tabela>;
  /** Uma entrada por quadro, na mesma ordem de `tabelas`. */
  secoes: Record<string, SecaoDeQuadros>;
  /** Os checkboxes de escopo (Alpha, Beta, S1, S2+), iguais em todos os quadros. */
  entregas?: { id: string; rotulo: string }[];
  /** O subtítulo do cabeçalho — o nome e o ícone vêm do item do menu. */
  subtitulo: ReactNode;
  /**
   * O seletor de abas, quando a tela dona divide o assunto em mais de um documento
   * (Personagem: Beta/Season 1 × Season 2+). Fica logo abaixo do cabeçalho, em todos
   * os estados — inclusive "Abrindo…" e erro, senão a aba que falhou prenderia a
   * pessoa nela.
   *
   * <p>Quem troca a aba deve trocar também o {@code key} deste componente: o estado
   * dos quadros é de UMA página, e reaproveitá-lo entre chaves gravaria o documento
   * de uma aba na outra.
   */
  abas?: ReactNode;
  /**
   * Os macro-títulos e a ordem dos quadros sob cada um. Com grupos, a página ganha um
   * índice clicável no alto e os quadros saem na ordem dos grupos; sem, saem na ordem
   * de `tabelas`, como sempre. Quadro que não está em grupo nenhum vem no fim, sem
   * macro-título — assim um quadro novo de fábrica nunca some da tela por esquecimento.
   */
  grupos?: GrupoDeQuadros[];
}) {
  const clienteDeConsultas = useQueryClient();
  const pagina = useQuery({
    queryKey: paginaApi.chaveDaPagina(chave),
    queryFn: () => paginaApi.ler(chave),
    staleTime: Infinity,
  });

  const [quadros, setQuadros] = useState<Record<string, Tabela> | null>(null);
  /** A versão de cada quadro **como esta tela a leu** — é o que vai no PATCH. */
  const [versoes, setVersoes] = useState<Record<string, number>>({});
  /** Quadros com edição ainda não gravada. É o que acende o botão. */
  const [sujos, setSujos] = useState<Record<string, boolean>>({});
  /** A reclamação do servidor, por quadro, quando alguém gravou antes. */
  const [conflitos, setConflitos] = useState<Record<string, string>>({});
  /** Qual quadro está no ar agora — o botão dele vira "Gravando…". */
  const [gravando, setGravando] = useState<string | null>(null);
  /** Quadros cujo aviso em destaque está aberto para escrita, mesmo vazio. */
  const [alertasAbertos, setAlertasAbertos] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!pagina.data || quadros) return;
    setQuadros(combinar(tabelas, pagina.data.conteudo));
    setVersoes(pagina.data.versoes ?? {});
    // `tabelas` é constante da página dona; segui-lo reexecutaria a leitura à toa.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagina.data, quadros]);

  const gravacao = useMutation({
    mutationFn: (q: string) =>
      paginaApi.salvarQuadro(chave, q, quadros?.[q], versoes[q] ?? null),
  });

  async function salvar(quadro: string) {
    setGravando(quadro);
    try {
      const salva = await gravacao.mutateAsync(quadro);
      /*
       * Só a versão DESTE quadro é atualizada, e a distinção não é detalhe.
       *
       * A resposta traz o mapa inteiro, e adotá-lo faria a tela aceitar como "lida" a
       * versão de um quadro que outra pessoa gravou enquanto eu editava — e o meu
       * próximo Salvar naquele quadro passaria pela trava e apagaria o trabalho dela.
       * A versão de um quadro só avança quando fui eu quem gravou, ou quando reli.
       */
      setVersoes((a) => ({ ...a, [quadro]: salva.versoes?.[quadro] ?? (a[quadro] ?? 0) + 1 }));
      setSujos((a) => ({ ...a, [quadro]: false }));
      setConflitos((a) => ({ ...a, [quadro]: '' }));
      // O log acabou de ganhar uma linha; sem isto ela só apareceria no próximo F5, e
      // um histórico que não mostra o que você acabou de fazer não parece confiável.
      void clienteDeConsultas.invalidateQueries({
        queryKey: paginaApi.chaveDoHistorico(chave),
      });
    } catch (erro) {
      /*
       * 409 é o caso previsto — alguém gravou no meio —, e é o único que não pode virar
       * "tente de novo": tentar de novo com a mesma versão apagaria o trabalho do outro.
       * A saída é reler o quadro, e é isso que o botão ao lado da mensagem faz.
       */
      setConflitos((a) => ({
        ...a,
        [quadro]:
          erro instanceof ErroApi && erro.status === 409
            ? erro.message
            : `Não consegui gravar: ${erro instanceof Error ? erro.message : 'erro desconhecido'}.`,
      }));
    } finally {
      setGravando(null);
    }
  }

  /**
   * Traz a versão do servidor para um quadro, descartando a edição local dele.
   *
   * <p>Só deste quadro: recarregar a página inteira jogaria fora o que a pessoa escreveu
   * nos outros, e o conflito é de um só.
   */
  async function relerQuadro(quadro: string) {
    const fresca = (await pagina.refetch()).data;
    if (!fresca) return;
    const doServidor = combinar(tabelas, fresca.conteudo)[quadro];
    setQuadros((a) => (a ? { ...a, [quadro]: doServidor } : a));
    // Só a deste quadro, pela mesma razão do `salvar`: os outros continuam valendo o
    // que esta tela leu, e é isso que protege quem estiver editando um deles.
    setVersoes((a) => ({ ...a, [quadro]: fresca.versoes?.[quadro] ?? a[quadro] ?? 0 }));
    setSujos((a) => ({ ...a, [quadro]: false }));
    setConflitos((a) => ({ ...a, [quadro]: '' }));
  }

  /*
   * O erro vem antes da espera, e não depois: quando a leitura falha, `quadros` nunca sai
   * de nulo — invertido, a tela diria "Abrindo…" para sempre e a mensagem de erro seria
   * código morto.
   */
  if (pagina.error) {
    return (
      <Moldura subtitulo={subtitulo} abas={abas}>
        <p className="pq-espera erro">
          {pagina.error instanceof Error ? pagina.error.message : 'Não foi possível abrir.'}
        </p>
      </Moldura>
    );
  }

  if (!quadros) {
    return (
      <Moldura subtitulo={subtitulo} abas={abas}>
        <p className="pq-espera">Abrindo…</p>
      </Moldura>
    );
  }

  /** O título que vale agora: o que o time escreveu; senão o de fábrica; senão a chave. */
  const tituloDe = (quadro: string) =>
    quadros[quadro]?.titulo ?? secoes[quadro]?.titulo ?? quadro;

  /*
   * A ordem de exibição: pelos grupos quando há, e o que sobrar vem no fim, sem
   * macro-título — um quadro de fábrica novo que ninguém pôs em grupo ainda precisa
   * aparecer, senão some da tela por esquecimento e ninguém repara.
   */
  const agrupados = new Set((grupos ?? []).flatMap((g) => g.quadros));
  const soltos = Object.keys(tabelas).filter((q) => !agrupados.has(q));
  const blocos: { id?: string; titulo?: string; quadros: string[] }[] = [
    ...(grupos ?? []).map((g) => ({
      id: g.id,
      titulo: g.titulo,
      quadros: g.quadros.filter((q) => q in tabelas),
    })),
    ...(soltos.length ? [{ quadros: soltos }] : []),
  ];
  /*
   * O índice existe sempre — é o padrão das três páginas (pedido do Theo, 19/08/2026).
   * Com grupos, uma coluna por macro-título; sem, uma lista só, na ordem da página.
   */
  const indice: { id?: string; titulo?: string; quadros: string[] }[] = grupos
    ? blocos.filter((b) => b.titulo)
    : [{ quadros: Object.keys(tabelas) }];

  const secao = (quadro: string, definicao: SecaoDeQuadros) => {
    const dados = quadros[quadro];
    const mudar = (nova: Tabela) => {
      setQuadros((a) => (a ? { ...a, [quadro]: nova } : a));
      setSujos((a) => ({ ...a, [quadro]: true }));
    };
    /*
     * A caixa de aviso só aparece quando há aviso — ou quando alguém pediu para
     * escrever um. Campos vermelhos vazios espalhados pela página gritariam por
     * atenção que não existe, e é justamente o contrário do que a cor serve para dizer.
     */
    const comAlerta = Boolean(dados.alerta) || Boolean(alertasAbertos[quadro]);

    return (
      <>
        <textarea
          className="pq-texto"
          rows={2}
          value={dados.texto ?? ''}
          placeholder="O que esta seção apresenta…"
          aria-label={`Texto da seção ${quadro}`}
          onChange={(e) => mudar(t.escreverTexto(dados, e.target.value))}
        />

        {comAlerta ? (
          <textarea
            className="pq-texto aviso"
            rows={2}
            value={dados.alerta ?? ''}
            placeholder="O que já quebrou, ou está prestes a quebrar…"
            aria-label={`Aviso em destaque da seção ${quadro}`}
            onChange={(e) => mudar(t.escreverAlerta(dados, e.target.value))}
          />
        ) : (
          <button
            type="button"
            className="pq-mais-aviso"
            onClick={() => setAlertasAbertos((a) => ({ ...a, [quadro]: true }))}
          >
            + aviso em destaque
          </button>
        )}

        <TabelaEditavel
          tabela={dados}
          aoMudar={mudar}
          rotuloDoItem={definicao.rotuloDoItem}
          numerada={definicao.numerada}
          /*
           * A coluna de cor aparece onde já há cor — os quadros cujo conteúdo de fábrica
           * agrupa por parentesco. Ver {@link SecaoDeQuadros#cores}.
           */
          cores={dados.familias ? definicao.cores : undefined}
          entregas={entregas}
          icone={definicao.iconeDaLinha}
        />
        <BarraDeGravacao
          sujo={Boolean(sujos[quadro])}
          gravando={gravando === quadro}
          conflito={conflitos[quadro]}
          aoSalvar={() => void salvar(quadro)}
          aoReler={() => void relerQuadro(quadro)}
        />
      </>
    );
  };

  return (
    <Moldura subtitulo={subtitulo} abas={abas}>
      <p className="pq-estado">
        Tudo é editável — a célula, o <b>nome da coluna</b> e quantas colunas há. Cada
        quadro tem o próprio <b>Salvar</b> e só grava a si mesmo, então duas pessoas podem
        escrever na página ao mesmo tempo.
        {pagina.data?.atualizadoPor && <> Última gravação por <b>{pagina.data.atualizadoPor}</b>.</>}
      </p>

      <nav className="pq-indice" aria-label="Índice da página">
        {indice.map((g) => (
          <div key={g.id ?? 'todos'} className="pq-indice-grupo">
            {g.titulo && <b>{g.titulo}</b>}
            <ul>
              {g.quadros.map((quadro) => (
                <li key={quadro}>
                  <a href={`#${quadro}`}>
                    {secoes[quadro]?.icone && (
                      <span className="pq-icone">{secoes[quadro].icone}</span>
                    )}
                    {tituloDe(quadro)}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>

      {blocos.map((bloco) => (
        <div key={bloco.id ?? '—'} className="pq-grupo">
          {bloco.titulo && <h2 className="pq-macro">{bloco.titulo}</h2>}
          {bloco.quadros.map((quadro) => {
            const definicao = secoes[quadro];
            return (
              <section
                key={quadro}
                id={quadro}
                className={`pq-secao${definicao.destaque ? ' destaque' : ''}`}
              >
                {/*
                  O título é campo, como a prosa: o de fábrica é só o que aparece
                  antes de o time reescrever. O `h2` fica em volta para o fio dourado e
                  o tamanho continuarem os mesmos — é o mesmo título, só que editável.
                */}
                <h2>
                  <span className="pq-cabecalho-secao">
                    {definicao.icone && <span className="pq-icone">{definicao.icone}</span>}
                    <input
                      className="pq-titulo"
                      value={tituloDe(quadro)}
                      aria-label={`Título da seção ${definicao.titulo}`}
                      onChange={(e) => {
                        const nova = t.escreverTitulo(quadros[quadro], e.target.value);
                        setQuadros((a) => (a ? { ...a, [quadro]: nova } : a));
                        setSujos((a) => ({ ...a, [quadro]: true }));
                      }}
                    />
                  </span>
                </h2>
                {secao(quadro, definicao)}
              </section>
            );
          })}
        </div>
      ))}

      <LogDeAlteracoes
        chave={chave}
        versoes={versoes}
        tituloDaSecao={tituloDe}
        aoRestaurar={(quadro) => void relerQuadro(quadro)}
      />
    </Moldura>
  );
}

/**
 * O log de alterações da página: quem mudou qual seção, quando, e o botão de desfazer.
 *
 * <p><b>Restaurar é gravar de novo</b>, com o conteúdo antigo. A linha restaurada não
 * some do log, e a restauração aparece nele como mais uma alteração — é o que torna
 * desfazer também desfazível, e o que impede o histórico de mentir sobre quem repôs
 * aquele texto.
 *
 * <p>Ele carrega sozinho, à parte da página: é a última coisa da tela, quase sempre não
 * é lido, e travar a abertura da página por causa dele seria pagar caro por informação
 * que quase ninguém pediu.
 */
function LogDeAlteracoes({
  chave,
  versoes,
  tituloDaSecao,
  aoRestaurar,
}: {
  chave: string;
  /** A versão que a tela leu de cada quadro — vai como trava na restauração. */
  versoes: Record<string, number>;
  /** Traduz a chave do quadro para o título — um log gravado antes de uma seção sair
   *  da página continua legível, porque a tradução cai na própria chave. */
  tituloDaSecao: (quadro: string) => string;
  aoRestaurar: (quadro: string) => void;
}) {
  const clienteDeConsultas = useQueryClient();
  const [erro, setErro] = useState('');
  const [restaurando, setRestaurando] = useState<number | null>(null);

  const log = useQuery({
    queryKey: paginaApi.chaveDoHistorico(chave),
    queryFn: () => paginaApi.historico(chave),
    staleTime: Infinity,
  });

  async function restaurar(alteracao: paginaApi.Alteracao) {
    setRestaurando(alteracao.id);
    setErro('');
    try {
      await paginaApi.restaurar(chave, alteracao.id, versoes[alteracao.quadro] ?? null);
      await clienteDeConsultas.invalidateQueries({
        queryKey: paginaApi.chaveDoHistorico(chave),
      });
      // Traz o quadro restaurado para a tela — sem isto, a página continuaria
      // mostrando o texto antigo e o time gravaria por cima do que acabou de repor.
      aoRestaurar(alteracao.quadro);
    } catch (e) {
      setErro(
        e instanceof ErroApi && e.status === 409
          ? e.message
          : `Não consegui restaurar: ${e instanceof Error ? e.message : 'erro desconhecido'}.`
      );
    } finally {
      setRestaurando(null);
    }
  }

  return (
    <section className="pq-secao">
      <h2>Log de alterações</h2>
      <p className="pq-nota">
        Cada gravação de seção deixa uma linha aqui. <b>Restaurar</b> traz o conteúdo
        daquela gravação de volta, como uma alteração nova — nada é apagado do log, e
        desfazer também pode ser desfeito.
      </p>

      {erro && <p className="pq-log-erro">{erro}</p>}

      {log.isLoading && <p className="pq-espera">Carregando o histórico…</p>}

      {log.error && (
        <p className="pq-log-erro">
          {log.error instanceof Error ? log.error.message : 'Não foi possível ler o histórico.'}
        </p>
      )}

      {log.data?.length === 0 && (
        <p className="pq-vazio-log">
          Ninguém alterou nada ainda. A primeira gravação de qualquer seção aparece aqui.
        </p>
      )}

      {log.data && log.data.length > 0 && (
        <div className="te-rolagem">
          <table className="pq-log">
            <thead>
              <tr>
                <th scope="col">Quando</th>
                <th scope="col">Quem</th>
                <th scope="col">Seção</th>
                <th scope="col">Versão</th>
                <th scope="col" />
              </tr>
            </thead>
            <tbody>
              {log.data.map((a) => (
                <tr key={a.id}>
                  <td>{quando(a.criadoEm)}</td>
                  <td>{a.autor ?? '—'}</td>
                  <td>{tituloDaSecao(a.quadro)}</td>
                  <td className="pq-log-versao">v{a.versao}</td>
                  <td>
                    <button
                      type="button"
                      className="pq-restaurar"
                      onClick={() => void restaurar(a)}
                      disabled={restaurando !== null}
                      title={`Trazer "${tituloDaSecao(a.quadro)}" de volta ao que era nesta gravação`}
                    >
                      {restaurando === a.id ? 'Restaurando…' : 'Restaurar'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/**
 * A data como quem lê a espera: dia, mês e hora, no fuso do navegador.
 *
 * <p>O servidor manda ISO-8601 em UTC. Mostrar isso cru faria o log dizer 23:31 para uma
 * gravação das 20:31 — e um histórico com hora errada é pior que um sem hora, porque
 * ninguém desconfia dele.
 */
function quando(iso: string): string {
  const data = new Date(iso);
  if (Number.isNaN(data.getTime())) return iso;
  return data.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * O rodapé de gravação de um quadro: o botão, e o que houve com o último clique.
 *
 * <p><b>O botão fica desabilitado sem edição pendente</b>, e o texto ao lado diz por
 * quê. Um "Salvar" sempre clicável numa página de muitos quadros ensina a clicar em
 * todos por via das dúvidas — e cada clique desses é uma gravação que sobe a versão do
 * quadro e derruba, com 409, quem estiver editando aquele mesmo quadro do outro lado.
 *
 * <p>O conflito não oferece "tentar de novo": tentar de novo com a mesma versão é
 * exatamente o que apagaria o trabalho da outra pessoa. A única saída oferecida é
 * <b>reler o quadro</b>, e ela avisa que o que está na tela se perde.
 */
function BarraDeGravacao({
  sujo,
  gravando,
  conflito,
  aoSalvar,
  aoReler,
}: {
  sujo: boolean;
  gravando: boolean;
  conflito?: string;
  aoSalvar: () => void;
  aoReler: () => void;
}) {
  return (
    <div className="pq-barra">
      <button
        type="button"
        className="pq-salvar"
        onClick={aoSalvar}
        disabled={!sujo || gravando}
      >
        {gravando ? 'Gravando…' : 'Salvar quadro'}
      </button>

      {conflito ? (
        <span className="pq-conflito">
          {conflito}{' '}
          <button type="button" className="pq-reler" onClick={aoReler}>
            Reler este quadro
          </button>{' '}
          <i>— o que você escreveu aqui se perde.</i>
        </span>
      ) : (
        <span className="pq-pendencia">
          {sujo ? 'Há mudanças não gravadas neste quadro.' : 'Sem mudanças a gravar.'}
        </span>
      )}
    </div>
  );
}

/**
 * O quadro da página — cabeçalho e largura —, comum aos três estados.
 *
 * <p>O subtítulo é o mesmo em "abrindo", em erro e na página cheia, e escrevê-lo três
 * vezes seria três chances de um deles ficar para trás numa edição. O nome e o ícone
 * não estão aqui: quem responde por eles é o item do menu, via {@link TituloDaPagina}.
 */
function Moldura({
  subtitulo,
  abas,
  children,
}: {
  subtitulo: ReactNode;
  abas?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="pq">
      <TituloDaPagina>{subtitulo}</TituloDaPagina>
      {abas}
      {children}
    </div>
  );
}
